<script setup lang="ts">
import { computed } from 'vue'
import { NModal, NButton, NAlert, useMessage } from 'naive-ui'
import { useApiTokensStore } from '../stores/api_tokens'

// One-time reveal of a freshly minted token. The store keeps the raw
// value in memory only on the in-flight record — once the modal
// acknowledges, the store forgets it and the token can never be
// recovered. Mirrors the rake task's "shown once" contract.

const props = defineProps<{ show: boolean }>()
const emit = defineEmits<{ 'update:show': [value: boolean] }>()

const store = useApiTokensStore()
const message = useMessage()

const raw = computed(() => store.lastCreatedRaw)

async function copy() {
  if (!raw.value) return
  try {
    await navigator.clipboard.writeText(raw.value)
    message.success('Token copied to clipboard')
  } catch {
    // Older browsers / non-secure contexts fall back to manual copy.
    // The token is still visible in the textarea above.
    message.warning('Clipboard not available — select and copy manually')
  }
}

function acknowledge() {
  store.forgetLastCreatedRaw()
  emit('update:show', false)
}
</script>

<template>
  <NModal
    :show="show && raw !== null"
    preset="dialog"
    title="Copy this token"
    style="width: 640px"
    :close-on-esc="false"
    :mask-closable="false"
    @update:show="(v: boolean) => emit('update:show', v)"
  >
    <NAlert type="warning" :show-icon="false" class="mb-3">
      This is the only time the cleartext value will be shown. Paste it into
      <code class="font-mono text-xs">~/.config/superbrain/config.yml</code>
      (or your client's credential store) before closing.
    </NAlert>

    <pre
      v-if="raw"
      class="font-mono text-xs bg-black/40 rounded p-3 select-all break-all whitespace-pre-wrap m-0"
      data-testid="api-token-raw"
      >{{ raw }}</pre>

    <template #action>
      <div class="flex gap-2 justify-end">
        <NButton :disabled="!raw" @click="copy" data-testid="api-token-copy"> Copy </NButton>
        <NButton type="primary" @click="acknowledge" data-testid="api-token-acknowledge"> I saved it </NButton>
      </div>
    </template>
  </NModal>
</template>
